/**
 * This is a class for representing dynamic asset comment.
 */
import { Box3, Euler, MathUtils, Mesh, Object3D, Quaternion, Vector2, Vector3 } from 'three';
import { CSS2DObject } from 'three/examples/jsm/renderers/CSS2DRenderer';

import { PoiModel } from '../../../../../typings/api/skymap/rest/v1/.common';
import { PoiPatchPayload } from '../../../../../typings/api/skymap/rest/v1/poi';
import {
  createLabel,
  removeLabel,
  updateLabel,
} from '../../../react/components/skyview-new/asset-menu/components/label-renderer/label-factory';
import { PoiLabelManager } from '../../../react/components/skyview-new/asset-menu/components/label-renderer/labels/poi-labels/poi-label-manager';
import { CloudFrontSigner } from '../../../utils/cloud-front-signer/cloud-front-signer';
import { isDefined } from '../../../utils/variables';
import { publish, SubscriptionTopic } from '../../messaging/pubsub';
import { AssetLoadingStatus, AssetType, ElementObj } from '../element-obj';
import { PoiCircleIndicator } from './poi-circle-indicator';

export type PoiProperties = Omit<PoiModel, 'id'> & {
  id?: string;
  visible?: boolean;
};

// These are the available names of the image resources available for a Poi with associated thumbnail.
export type PoiThumbNailsResources = 'original' | '64x_' | '512x_' | '1024x_';

export class Poi extends ElementObj {
  async onStateVisibilityChanged() {
    // Do nothing
    return Promise.resolve();
  }

  async toggleVisibility(visible?: boolean) {
    const state = visible ?? !this.visible;
    if (state) {
      if (!this.css2label && !this.merged && !this.culled) {
        this.createLabel();
      }
    } else {
      if (this.css2label) {
        this.removeLabel();
      }
    }
    if (this.visible === state) {
      return;
    }
    await super.toggleVisibility(state);
  }

  private _culled = false;
  set culled(val: boolean) {
    if (this._culled === val) {
      return;
    }
    this._culled = val;
    this.frustumCulled = val;

    if (val) {
      this.removeLabel();
    } else if (this.visible && !this.merged) {
      this.createLabel();
    }
  }

  get culled() {
    return this._culled;
  }

  getPanoramaRotationEuler(yawOffsetDeg = 0) {
    const panoramaMetadata = this._properties.thumbnailImage?.image360Metadata;
    if (!isDefined(panoramaMetadata) || !this._properties.is360) {
      return new Quaternion();
    }

    const yaw = MathUtils.degToRad(-((panoramaMetadata.poseHeadingDegrees ?? 0) + yawOffsetDeg));
    const pitch = 0;
    const roll = 0;

    const eulerYXZ = new Euler(pitch, yaw, roll, 'YXZ');
    return new Quaternion().setFromEuler(eulerYXZ);
  }

  get has360RotationMetadata() {
    return isDefined(this._properties.thumbnailImage?.image360Metadata?.poseHeadingDegrees);
  }

  get properties() {
    return this._properties;
  }

  private cloudFrontSigner?: CloudFrontSigner;
  private css2label?: CSS2DObject;
  private circleIndicator?: PoiCircleIndicator;

  private _isMerged = false;
  set merged(val: boolean) {
    if (this._isMerged === val) {
      return;
    }
    this._isMerged = val;

    if (val) {
      this.removeLabel();
    } else if (this.visible && !this.culled) {
      this.createLabel();
    }
  }

  get merged() {
    return this._isMerged;
  }

  displayCircleIndicator(state: boolean) {
    if (isDefined(this.circleIndicator)) {
      this.circleIndicator.visible = state;
    }
  }

  onMetaDataUpdated?: () => void;

  constructor(
    private _properties: PoiProperties,
    insertionPoint: Vector3 | undefined,
  ) {
    super(_properties.timelineContainerId ?? null);

    this.assetId = _properties.id;

    this.freeze = {
      translate: false,
      rotateZ: true,
      rotateXY: true,
      scale: true,
    };

    this.updatePosition(_properties, insertionPoint);

    const visible = _properties.visible ?? false;
    void this.setStateVisibility(visible);

    if (isDefined(this.properties.thumbnailImage)) {
      this.cloudFrontSigner = new CloudFrontSigner(
        this.properties.thumbnailImage.resourceUrls.signedRequest,
      );
    }

    PoiLabelManager.instance.register(this.uuid, this);

    this.name = _properties.title;
    if (visible) {
      try {
        this.loadingStatus = 'loading';
        this.createLabel();
        this.loadingStatus = 'loaded';
      } catch (err) {
        this.loadingStatus = 'error';
      } finally {
        publish(SubscriptionTopic.AssetChanged, this);
      }
    }

    if (!this.has360RotationMetadata) {
      this.rotateOnAxis(new Vector3(0, 1, 0), MathUtils.degToRad(this.properties.headingDeg ?? 0));
    }

    this._assetTagIds = _properties.assetTags?.map((tag) => tag.id) ?? [];
  }

  initialLoadingStatus(): AssetLoadingStatus {
    return 'loaded';
  }

  createLabel() {
    if (this.css2label) {
      return;
    }
    this.css2label = createLabel({ type: 'poi', poi: this });
    this.add(this.css2label);
  }

  removeLabel() {
    if (isDefined(this.css2label)) {
      this.remove(this.css2label);
      removeLabel({ type: 'poi', poi: this });

      this.css2label = undefined;
    }
  }

  updateLabel() {
    if (!isDefined(this.css2label)) {
      return;
    }

    updateLabel(this);
  }

  hasLabel() {
    return isDefined(this.css2label);
  }

  get assetType(): AssetType {
    return 'poi';
  }

  recalculate(idx: number, newPoint: Vector3) {
    if (!newPoint) {
      return;
    }

    if (idx > -1) {
      this.position.x = newPoint.x;
      this.position.y = newPoint.y;
      this.position.z = newPoint.z;
    }

    const wgs84 = this.calculateWGS84Coords(new Vector2(this.position.x, this.position.y));
    if (wgs84 !== undefined) {
      this.wgs84 = {
        lng: wgs84.x,
        lat: wgs84.y,
      };
    }

    this.updateMas();
    this.onMetaDataUpdated?.();
  }

  onTransformed() {
    this.updateMas();
  }

  getPointWorldPosition(idx: number) {
    if (idx !== -1) {
      return this.position;
    }
  }

  setHover(state: boolean) {
    // this.circleIndicator?.setHover(state, true);
  }

  dispose() {
    function disposeNode(node: Object3D) {
      for (const child of node.children) {
        disposeNode(child);
        const childAsMesh = child as Mesh;
        if (childAsMesh.geometry) {
          childAsMesh.geometry.dispose();
        }
        if (childAsMesh.material) {
          const materalArray = Array.isArray(childAsMesh.material)
            ? childAsMesh.material
            : [childAsMesh.material];
          for (const material of materalArray) {
            material.dispose();
          }
        }
      }
    }
    disposeNode(this);

    removeLabel({ type: 'poi', poi: this });

    PoiLabelManager.instance.unregister(this.uuid);
  }

  onSelect() {
    super.onSelect();
  }

  onDeselect() {
    super.onDeselect();
  }

  /**
   * @description Returns the data to be saved for this object
   */
  getSaveData(): Promise<PoiPatchPayload> {
    const wgs84 = this.calculateWGS84Coords(new Vector2(this.position.x, this.position.y));
    this.updateMas();

    const getHeadingDegFromLocalQuat = (q: Quaternion): number => {
      // Sphere forward (+Z in its local space) expressed in parent-local frame.
      const f = new Vector3(0, 0, 1).applyQuaternion(q).normalize();
      // Project onto parent XZ-plane (yaw is around parent +Y).
      const fXZ = new Vector2(f.x, f.z);
      if (fXZ.lengthSq() < 1e-12) {
        // Degenerate, shouldn't happen in normal pano use.
        return 0;
      }

      // Angle from +Z toward +X (right-handed)
      const yawRad = Math.atan2(fXZ.x, fXZ.y);
      // Normalize to [0, 360)
      return (MathUtils.radToDeg(yawRad) + 360) % 360;
    };

    const headingDeg = getHeadingDegFromLocalQuat(this.quaternion);

    return Promise.resolve({
      comment: this.properties.comment,
      is360: this.properties.is360,
      title: this.properties.title,
      wgs84Longitude: wgs84?.x,
      wgs84Latitude: wgs84?.y,
      metersAboveMeanSeaLevel: this.MAS,
      headingDeg,
      // Currently, we do not change the pitch or roll,
      // as not applicable at this state.
      pitchDeg: undefined, // this.rotation.x,
      rollDeg: undefined, // this.rotation.z,
    });
  }

  onSaved(data?: PoiProperties) {
    if (!isDefined(data)) {
      return;
    }

    this.name = data.title;

    this._properties = data;

    this.updatePosition(data);
  }

  getNativeAssetData() {
    return {
      uuid: this.uuid,
      name: this.name,
      visible: this.visible,
      comment: this.properties.comment,
      author: 'Okänd',
      edited: 'Okänd',
    };
  }

  getThumbnailResourceUrl(resourceName: PoiThumbNailsResources) {
    return this.cloudFrontSigner?.getSignedRequest(resourceName);
  }

  getImagePreviewUrl(): string | undefined {
    return this.properties.thumbnailImage?.originalPreviewUrl;
  }

  is360Poi() {
    return isDefined(this.properties.thumbnailImage) && this.properties.is360;
  }

  /**
   * @param toWorldCoordinates
   * @description Generate the elemnt objects bounding box
   * @returns {Box3}
   */
  override generateBoundingBox(toWorldCoordinates = false) {
    const position = this.position;
    const bbMin = position.clone().add(new Vector3(-2, -2, -2));
    const bbMax = position.clone().add(new Vector3(2, 2, 2));

    return new Box3(bbMin, bbMax);
  }

  override patch(poi: Poi) {
    this.timelineContainerId = poi.timelineContainerId;

    this._properties = poi._properties;
    this.assetId = poi._properties.id;

    this.updatePosition(poi._properties);

    if (isDefined(poi._properties.thumbnailImage)) {
      this.cloudFrontSigner = new CloudFrontSigner(
        poi._properties.thumbnailImage.resourceUrls.signedRequest,
      );
    } else {
      delete this.cloudFrontSigner;
      this.cloudFrontSigner = undefined;
    }

    this.name = poi._properties.title;

    this._assetTagIds = poi._properties.assetTags?.map((tag) => tag.id) ?? [];

    try {
      this.loadingStatus = 'loading';
      this.updateLabel();
      this.loadingStatus = 'loaded';
    } catch (err) {
      this.loadingStatus = 'error';
    } finally {
      publish(SubscriptionTopic.AssetChanged, this);
    }
  }

  override setPosition(newPosition: Vector3) {
    this.position.copy(newPosition);
    this.updateMas();
  }

  private updatePosition(poiProperties: PoiProperties, insertionPoint?: Vector3) {
    this.wgs84 = {
      lat: Number(poiProperties.wgs84Latitude),
      lng: Number(poiProperties.wgs84Longitude),
    };

    this.MAS = poiProperties.metersAboveMeanSeaLevel;

    const localPos = this.calculateLocalPosition();
    const localHeight = this.calculateLocalHeight();
    if (localPos) {
      this.position.x = localPos.x;
      this.position.y = localPos.y;
      this.position.z = localHeight ?? 0;
    } else if (isDefined(insertionPoint)) {
      this.position.x = insertionPoint.x;
      this.position.y = insertionPoint.y;
      this.position.z = insertionPoint.z;
    }

    // If no wgs84 is defined, it is last saved with the deprecated settings
    // Therefore, calculate wgs84 coords and resave it to store it with the new settings.
    if (this.wgs84 === undefined) {
      const wgs84 = this.calculateWGS84Coords(new Vector2(this.position.x, this.position.y));
      if (wgs84) {
        this.wgs84 = {
          lng: wgs84.x,
          lat: wgs84.y,
        };
      }
    }

    // If no MAS is defined, we calculate it based on the current origo and the z-position
    if (this.MAS === undefined) {
      this.MAS = this.calculateMAS();
    }
  }

  /** Re-compute MAS from the *baseline* plus the new height */
  private updateMas() {
    this.MAS = this.getMasBase() + this.getWorldPosition(new Vector3()).z;
  }
}
